import type { Optional } from '@festina-life/core'
import cx from 'classnames'
import {
  type ComponentProps,
  forwardRef,
  type ReactNode,
  useMemo,
  useRef,
} from 'react'

import { CircularSpinner } from '../../feedback/circularSpinner/CircularSpinner'
import { paperStyles } from '../../libs/paperStyles'
import { mergeRefs } from '../../libs/refs'
import {
  type PropsWithSafeExtra,
  safeRestProps,
} from '../../libs/safeRestProps'
import { typographyStyles } from '../../structure/typography/styles'
import { ClearButton } from '../internal/clearButton/ClearButton'
import { clearButtonStyles } from '../internal/clearButton/clearButtonStyles'
import { clearNativeInputValue } from '../internal/clearButton/clearNativeInputValue'
import { selectStyles } from '../select/selectStyles'
import { rawTextInputStyles } from './rawTextInputStyles'

export type RawTextInputProps = PropsWithSafeExtra<
  'input',
  | 'autoComplete'
  | 'autoFocus'
  | 'className'
  | 'defaultValue'
  | 'disabled'
  | 'id'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'onClick'
  | 'onError'
  | 'onFocus'
  | 'onKeyDown'
  | 'placeholder'
  | 'required',
  {
    isInError?: boolean
    /** Shows a spinner inside the input in place of the clear button. */
    isLoading?: boolean
    addOnProps?: {
      element: ReactNode
      className?: Optional<string>
    }
    value?: string
    isHidden?: boolean
    type?: 'text' | 'number' | 'hidden' | 'tel' | 'file'
    ref?: ComponentProps<'input'>['ref']
  }
>

/** Text input that can be used in Festina Forms to display a text input that follows our styling rules. May or may
 * not be wrapped by a FormInput component to add labels, descriptions and error messages around the input field.
 */
export const RawTextInput = forwardRef<HTMLInputElement, RawTextInputProps>(
  (
    {
      isInError = false,
      isLoading = false,
      addOnProps,
      disabled = false,
      type = 'text',
      value,
      className,
      isHidden,
      onFocus,
      extraProps = {},
      ...restProps // Needed since we can pass other props like aria-*, data-* etc. or spread that isn't type checked
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const mergedRef = useMemo(() => mergeRefs(inputRef, ref), [ref])
    const isClearable = !!value && !disabled && type !== 'hidden'
    const hasIcon = isClearable || isLoading

    return (
      <div className={cx('relative w-full flex', clearButtonStyles.group)}>
        {addOnProps && (
          <span
            className={cx(
              'flex items-center rounded-l-lg px-2 -me-px',
              paperStyles({ ringColor: 'grey', isFaded: true }),
              typographyStyles,
              addOnProps.className,
              'focus-within:z-1', // to ensure ring is behind the input field unless the addon is focused
            )}
          >
            {addOnProps.element}
          </span>
        )}

        <input
          {...safeRestProps(extraProps, restProps)}
          className={cx(
            rawTextInputStyles({
              isInError,
              isAfterAddOn: !!addOnProps,
              disabled,
            }),
            hasIcon && clearButtonStyles.inputPadding,
            className,
          )}
          data-autofocus
          onFocus={(event) => {
            event.target.select()
            onFocus?.(event)
          }}
          aria-invalid={isInError}
          disabled={disabled}
          ref={mergedRef}
          value={value ?? ''} // Fallback to '' makes sure the inputs are controlled always
          type={type}
        />
        {hasIcon && (
          <div
            className={cx(
              selectStyles.inputIconsWrapper,
              'z-2', // the focused input is raised to z-1 above the add-on
              isInError && 'text-error-500',
            )}
          >
            {isLoading ? (
              <CircularSpinner className="size-4 m-0.5" />
            ) : (
              <ClearButton
                onClear={() => {
                  if (inputRef.current) {
                    clearNativeInputValue(inputRef.current)
                  }
                }}
              />
            )}
          </div>
        )}
      </div>
    )
  },
)

RawTextInput.displayName = 'RawTextInput'
